import React, { useState, useEffect, useRef } from 'react';
import { ENV, SITE_URL } from '../../../config/index.js';
import axios from 'axios';

// results
import FuseData from '../results/FuseData';
import GeoData from '../results/GeoData';
import HistoricalData from '../results/HistoricalData';
import IgnitionThrustContext from '../results/IgnitionThrustContext';

// ux
import LoadingAnimation from '../ux/LoadingAnimation';
import LoadingAnimationSimple from '../ux/LoadingAnimationSimple';
import FusionCreator from '../results/FusionCreator';
import FusionCreatorMobile from '../results/FusionCreatorMobile';
import FuseHelpModal from '../ux/FuseHelpModal.js';

function parseTimeSeries(dataString) {
  const entries =
    dataString.match(/date:\s*([\d\/-]+),\s*time_score:\s*(\d+)\s*;?/g) || [];
  return entries.map((entry) => {
    const cleanEntry = entry.replace(/;\s*$/, ''); // Remove trailing semicolon
    const [datePart, scorePart] = cleanEntry
      .split(',')
      .map((part) => part.trim());
    return {
      date: datePart.split(':')[1].trim(),
      time_score: parseInt(scorePart.split(':')[1].trim(), 10)
    };
  });
}

function parseGeoData(rawData) {
  let textData = rawData.replace(/<[^>]*>/g, '');

  // Decode HTML entities
  textData = textData.replace(/&lt;/g, '<').replace(/&#038;/g, '&');

  // Split the data into entries based on semicolon delimiters
  const entries = textData
    .split(';')
    .map((entry) => entry.trim())
    .filter((entry) => entry);

  // Parse each entry into a structured format
  const parsedData = entries
    .map((entry) => {
      const parts = entry.split(',').map((part) => part.trim());
      if (parts.length < 2) return null; // Skip malformed entries

      let geo_score = '0'; // Default value
      let region = '';

      // Process both parts to check which one is geo_score and which one is region
      parts.forEach((part) => {
        if (part.includes('geo_score:')) {
          const scorePart = part.split(':')[1];
          if (scorePart) geo_score = scorePart.trim();
        } else if (part.includes('region:')) {
          const regionValue = part.split(':')[1];
          if (regionValue) region = regionValue.trim();
        }
      });

      // If geo_score is not a number, set it to '0'
      if (isNaN(parseFloat(geo_score))) {
        geo_score = '0';
      }

      return { geo_score, region };
    })
    .filter((entry) => entry); // Remove null entries resulting from malformed data

  return parsedData;
}

const Fuse = ({
  fuseLoading,
  fuseSavedData,
  fuseLoadingData,
  fuseLoadingList,
  handleOpenModal,
  fuseShareId,
  sidebarOpen,
  setSidebarOpen,
  handleViewChange,
  fetchProject,
  backInfo,
  pinImage,
  retryFetchFuseData,
  projects,
  createPitch,
  loadingPitchesArray,
  helpOpen,
  toggleHelp
}) => {
  const [loadingScore, setLoadingScore] = useState(false);
  const [ignitionThrust, setIgnitionThrust] = useState(null);
  const [ignitionContext, setIgnitionContext] = useState(null);
  const [loadingData, setLoadingData] = useState(false);
  const [brandName, setBrandName] = useState(null);
  const [celebrityName, setCelebrityName] = useState(null);
  const [predictedMediaMentions, setPredictedMediaMentions] = useState(null);
  const [predictedImpressions, setPredictedImpressions] = useState(null);
  const [avgMonthlyMentions, setAvgMonthlyMentions] = useState(null);
  const [frequency, setFrequency] = useState(null);
  const [loadingTimeSeries, setLoadingTimeSeries] = useState(false);
  const [brandTimeSeries, setBrandTimeSeries] = useState(null);
  const [celebrityTimeSeries, setCelebrityTimeSeries] = useState(null);
  const [loadingGeoData, setLoadingGeoData] = useState(false);
  const [brandGeoData, setBrandGeoData] = useState(null);
  const [celebrityGeoData, setCelebrityGeoData] = useState(null);
  const [loadingContent, setLoadingContent] = useState(false);
  const [isNewFuse, setIsNewFuse] = useState(false);
  const [fusionCreatorData, setFusionCreatorData] = useState(null);
  const [fuseSidebarOpen, setFuseSidebarOpen] = useState(false);
  const [mrFusionChat, setMrFusionChat] = useState({
    thread_id: '',
    messages: []
  });
  const [mrFusionThinking, setMrFusionThinking] = useState(false);
  const [inputs, setInputs] = useState({
    message: ''
  });
  const [projectFuseList, setProjectFuseList] = useState([]);
  const [connectedProject, setConnectedProject] = useState(null);

  const [createPitchOpen, setCreatePitchOpen] = useState(false);
  const [loadingPitch, setLoadingPitch] = useState(false);
  const [selectedFuses, setSelectedFuses] = useState([]);

  const [pitchLink, setPitchLink] = useState('');

  const [currentStep, setCurrentStep] = useState(1);

  // Toggle state for light/dark mode
  const [isDarkMode, setIsDarkMode] = useState(false);

  // Toggle handler
  const handleToggleChange = () => {
    setIsDarkMode(!isDarkMode);
  };

  // console log dark mode state
  useEffect(() => {
    console.log('Dark mode is:', isDarkMode ? 'on' : 'off');
  }, [isDarkMode]);

  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [mrFusionChat.messages]);

  const handleToggleSidebar = () => {
    setFuseSidebarOpen((prevFuseSidebarOpen) => {
      const isFuseSidebarOpening = !prevFuseSidebarOpen; // Toggle the fuse sidebar

      if (isFuseSidebarOpening) {
        // If opening fuse sidebar, close the main sidebar
        setSidebarOpen(false);
        // if no mrFusionChat.thread_id, start a new conversation
        if (!mrFusionChat.thread_id) startConversation();
      } else {
        // If closing fuse sidebar, you can choose to open main sidebar if needed
        setSidebarOpen(true); // This can be adjusted based on your needs
      }

      return isFuseSidebarOpening; // Update fuse sidebar state
    });
  };

  // if sidebarOpen is true, close the fuse sidebar
  useEffect(() => {
    if (sidebarOpen) {
      setFuseSidebarOpen(false);
    }
  }, [sidebarOpen]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setInputs((inputs) => ({ ...inputs, [name]: value }));
  };

  const startConversation = async () => {
    setMrFusionThinking(true);
    const dataToSend = {
      brand_name: brandName,
      celeb_name: celebrityName,
      collab_details: fusionCreatorData.fuse_ideas[0].fuse_idea
    };
    try {
      const resInitial = await axios.post(
        '/wp-json/custom/v1/proxy',
        {
          action: 'startConversation',
          data: dataToSend
        },
        {
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );
      setMrFusionChat({
        thread_id: resInitial.data.thread_id,
        messages: [resInitial.data.response]
      });
    } catch (error) {
      console.error(
        'API 6a error:',
        error.response ? error.response.data : error.message
      );
    }
    setMrFusionThinking(false);
  };

  const sendMessage = async () => {
    setMrFusionThinking(true);
    const dataToSend = {
      thread_id: mrFusionChat.thread_id,
      message: inputs.message
    };
    // update chat messages array with inputs.message
    setMrFusionChat((prev) => ({
      ...prev,
      messages: [...prev.messages, inputs.message]
    }));
    setInputs((prev) => ({ ...prev, message: '' }));
    try {
      const resResponse = await axios.post(
        '/wp-json/custom/v1/proxy',
        {
          action: 'sendMessage',
          data: dataToSend
        },
        {
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );
      let newMessage =
        resResponse.data.response || "I didn't get that, please try again.";

      setMrFusionChat((prev) => ({
        ...prev,
        messages: [...prev.messages, newMessage]
      }));
    } catch (error) {
      console.error(
        'API 6b error:',
        error.response ? error.response.data : error.message
      );
    }
    setMrFusionThinking(false);
  };

  // **Extract the `ideaID` of the current fuse**
  const ideaID = fuseSavedData.idea_id;

  // **Load saved data when component mounts or `fuseSavedData` changes**
  useEffect(() => {
    if (fuseSavedData.ignition_context) {
      setIgnitionContext(fuseSavedData.ignition_context);
    }
    if (fuseSavedData.ignition_thrust) {
      setIgnitionThrust(fuseSavedData.ignition_thrust);
    }
    if (fuseSavedData.brand_name) {
      setBrandName(fuseSavedData.brand_name);
    }
    if (fuseSavedData.celebrity_name) {
      setCelebrityName(fuseSavedData.celebrity_name);
    }
    if (fuseSavedData.predicted_media_mentions) {
      setPredictedMediaMentions(fuseSavedData.predicted_media_mentions);
    }
    if (fuseSavedData.predicted_impressions) {
      setPredictedImpressions(fuseSavedData.predicted_impressions);
    }
    if (fuseSavedData.avg_monthly_mentions) {
      setAvgMonthlyMentions(fuseSavedData.avg_monthly_mentions);
    }
    if (fuseSavedData.frequency) {
      setFrequency(fuseSavedData.frequency);
    }
    if (fuseSavedData.brand_timeseries) {
      setBrandTimeSeries(parseTimeSeries(fuseSavedData.brand_timeseries));
    }
    if (fuseSavedData.celeb_timeseries) {
      setCelebrityTimeSeries(parseTimeSeries(fuseSavedData.celeb_timeseries));
    }
    if (fuseSavedData.brand_geo) {
      setBrandGeoData(parseGeoData(fuseSavedData.brand_geo));
    }
    if (fuseSavedData.celeb_geo) {
      setCelebrityGeoData(parseGeoData(fuseSavedData.celeb_geo));
    }
    if (fuseSavedData.fuse_ideas && fuseSavedData.fuse_ideas.length > 0) {
      setFusionCreatorData(fuseSavedData);
    }
  }, [fuseSavedData]);

  // **Update data when `fuseLoadingData` changes**
  useEffect(() => {
    if (fuseLoadingData && fuseLoadingData[ideaID]) {
      const data = fuseLoadingData[ideaID];
      if (data.ignition_context) {
        setIgnitionContext(data.ignition_context);
      }
      if (data.ignition_thrust) {
        setIgnitionThrust(data.ignition_thrust);
      }
      if (data.predicted_media_mentions) {
        setPredictedMediaMentions(data.predicted_media_mentions);
      }
      if (data.predicted_impressions) {
        setPredictedImpressions(data.predicted_impressions);
      }
      if (data.avg_monthly_mentions) {
        setAvgMonthlyMentions(data.avg_monthly_mentions);
      }
      if (data.frequency) {
        setFrequency(data.frequency);
      }
      if (data.brand_timeseries) {
        setBrandTimeSeries(parseTimeSeries(data.brand_timeseries));
      }
      if (data.celeb_timeseries) {
        setCelebrityTimeSeries(parseTimeSeries(data.celeb_timeseries));
      }
      if (data.brand_geo) {
        setBrandGeoData(parseGeoData(data.brand_geo));
      }
      if (data.celeb_geo) {
        setCelebrityGeoData(parseGeoData(data.celeb_geo));
      }
      if (data.fuse_ideas && data.fuse_ideas.length > 0) {
        setFusionCreatorData(data);
      }
    }
  }, [fuseLoadingData, ideaID]);

  // **Update loading status based on data availability**
  useEffect(() => {
    const isInLoadingList =
      ideaID && fuseLoadingList.some((item) => item.idea_id === ideaID);
    if (isInLoadingList) {
      // set is new fuse to true
      setIsNewFuse(true);
      // ignition thrust
      if (!ignitionContext || !ignitionThrust) {
        setLoadingScore(true);
      } else {
        setLoadingScore(false);
      }
      // fuse data
      if (
        !predictedMediaMentions ||
        !predictedImpressions ||
        !avgMonthlyMentions ||
        !frequency
      ) {
        setLoadingData(true);
      } else {
        setLoadingData(false);
      }
      // time series
      if (!brandTimeSeries || !celebrityTimeSeries) {
        setLoadingTimeSeries(true);
      } else {
        setLoadingTimeSeries(false);
      }
      // geo data
      if (!brandGeoData || !celebrityGeoData) {
        setLoadingGeoData(true);
      } else {
        setLoadingGeoData(false);
      }
      // fusion creator content
      if (!fusionCreatorData) {
        setLoadingContent(true);
      } else {
        setLoadingContent(false);
      }
    } else {
      // ignition thrust
      if (!ignitionContext || !ignitionThrust) {
        setLoadingScore(false);
      } else {
        setLoadingScore(false);
      }
      // fuse data
      if (
        !predictedMediaMentions ||
        !predictedImpressions ||
        !avgMonthlyMentions ||
        !frequency
      ) {
        setLoadingData(false);
      } else {
        setLoadingData(false);
      }
      // time series
      if (!brandTimeSeries || !celebrityTimeSeries) {
        setLoadingTimeSeries(false);
      } else {
        setLoadingTimeSeries(false);
      }
      // geo data
      if (!brandGeoData || !celebrityGeoData) {
        setLoadingGeoData(false);
      } else {
        setLoadingGeoData(false);
      }
      // fusion creator content
      if (!fusionCreatorData) {
        setLoadingContent(false);
      } else {
        setLoadingContent(false);
      }
    }
  }, [
    fuseLoadingList,
    ignitionContext,
    ignitionThrust,
    predictedMediaMentions,
    predictedImpressions,
    avgMonthlyMentions,
    frequency,
    brandTimeSeries,
    celebrityTimeSeries,
    brandGeoData,
    celebrityGeoData,
    fusionCreatorData,
    ideaID
  ]);

  const handleRetry = () => {
    retryFetchFuseData(fuseSavedData.job_id, fuseSavedData.ID, ideaID);
  };

  const generateShareLink = (fuseSlug) => {
    return `${SITE_URL}/fuse/${fuseSlug}/`;
  };

  const copyToClipboard = (link) => {
    navigator.clipboard.writeText(link).then(
      () => {
        alert('Link copied to clipboard!');
      },
      (err) => {
        console.error('Could not copy text: ', err);
      }
    );
  };

  // fetch fuse list based on IdeaId
  // console log ideaId
  useEffect(() => {
    if (ideaID) {
      // Remove everything after the last dash
      const projectID = ideaID.split('-')[0];

      // Find the project based on the projectID
      const project = projects.find(
        (proj) => proj.ID === parseInt(projectID, 10)
      );

      // Safely check for project existence
      if (project) {
        setConnectedProject(project);

        // Safely check for fuse_list existence
        if (project.fuse_list && Array.isArray(project.fuse_list)) {
          setProjectFuseList(project.fuse_list);
        } else {
          setProjectFuseList([]);
        }
      } else {
        console.warn(`No project found with ID: ${projectID}`);
        setConnectedProject(null);
        setProjectFuseList([]);
      }
    }
  }, [ideaID, projects]);

  useEffect(() => {
    if (!connectedProject) {
      return; // If connectedProject is null, just return early.
    }
    if (connectedProject.pitch_list && connectedProject.pitch_list.length > 0) {
      setPitchLink(connectedProject.pitch_list[0].pitch_link);
    }
  }, [connectedProject]);

  const openCreatePitch = () => {
    setCreatePitchOpen(true);

    // find current fuse by matching fuseSavedData.ID with projectFuseList.ID
    const currentFuse = projectFuseList.find(
      (fuse) => fuse.ID === fuseSavedData.ID
    );
    // add the current fuse to the selectedFuses array

    setSelectedFuses([currentFuse]);
  };

  const closeCreatePitch = () => {
    setCreatePitchOpen(false);
    setSelectedFuses([]);
  };

  const handleCreatePitch = () => {
    createPitch(connectedProject.ID, brandName, selectedFuses, isDarkMode);
    closeCreatePitch();
  };

  const maxSelection = 3;

  const handleItemClick = (fuse) => {
    const isSelected = selectedFuses.includes(fuse);

    if (isSelected) {
      // Remove fuse if it's already selected
      setSelectedFuses((prevFuses) =>
        prevFuses.filter((item) => item !== fuse)
      );
    } else if (selectedFuses.length < maxSelection) {
      // Add fuse if it's not already selected and under the limit
      setSelectedFuses((prevFuses) => [...prevFuses, fuse]);
    }
  };

  // if connectedProject.ID is in the loadingPitchesArray, set loadingPitch to true
  useEffect(() => {
    if (!connectedProject) return;
    if (loadingPitchesArray.includes(connectedProject.ID)) {
      setLoadingPitch(true);
    } else {
      setLoadingPitch(false);
    }
  }, [loadingPitchesArray, connectedProject]);

  // tutorial functions
  const totalSteps = 8;
  const nextHelpStep = () => {
    if (currentStep < totalSteps) {
      setCurrentStep((prevStep) => prevStep + 1);
    } else {
      endHelpTutorial();
    }
  };

  const prevHelpStep = () => {
    if (currentStep > 1) {
      setCurrentStep((prevStep) => prevStep - 1);
    }
  };

  const endHelpTutorial = () => {
    toggleHelp();
    setCurrentStep(1);
  };

  // get the width of .header-left
  const headerLeft = document.querySelector('.header-left');
  const headerLeftWidth = headerLeft ? headerLeft.offsetWidth : 0;

  return (
    <div
      className={
        fuseSidebarOpen && !sidebarOpen
          ? 'fuselight-fuse-container fuse-sidebar-open'
          : sidebarOpen
          ? 'fuselight-fuse-container main-sidebar-open'
          : 'fuselight-fuse-container'
      }
    >
      {createPitchOpen && (
        <div className="create-pitch-modal fuselight-v5-modal-container">
          <div className="fuselight-v5-modal">
            <button className="close" onClick={closeCreatePitch}></button>
            <p>Select up to 3 fusions to add to the pitch</p>
            <div className="fuse-list">
              {projectFuseList.map((fuse, fuseIndex) => {
                return (
                  <div
                    className={
                      selectedFuses.includes(fuse) ? 'fuse selected' : 'fuse'
                    }
                    key={fuseIndex}
                    onClick={() => handleItemClick(fuse)}
                  >
                    <div className="fuse-content">
                      {fuse.fuse_title ? (
                        <h3>{fuse.fuse_title}</h3>
                      ) : (
                        <h3>
                          {fuse.brand_name} x {fuse.celebrity_name}
                        </h3>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="create-pitch-buttons">
              <label className="toggle-container">
                <input
                  type="checkbox"
                  checked={isDarkMode}
                  onChange={handleToggleChange}
                />
                <span className="slider"></span>
                <span className="mode-label">
                  {isDarkMode ? 'Dark' : 'Light'}
                </span>
              </label>
              <button
                className="create-pitch btn btn--secondary-outline"
                onClick={handleCreatePitch}
                disabled={selectedFuses.length === 0}
              >
                Create
              </button>
            </div>
          </div>
        </div>
      )}
      <div className="launchpad-bg">
        <div></div>
        <div></div>
        <div></div>
      </div>
      {fuseLoading ? (
        <LoadingAnimation label="Loading Fusion" />
      ) : (
        <>
          <div className="fuse-main">
            <div className="fuse-header">
              <div className="header-left">
                <button
                  className="btn btn--primary-outline back-button"
                  onClick={
                    backInfo && backInfo.ID
                      ? () => fetchProject(backInfo)
                      : () => handleViewChange('fusions')
                  }
                >
                  Back
                </button>
                <h1>{fuseSavedData.fuse_title}</h1>
                <button
                  className="btn btn--secondary-outline share-button"
                  onClick={() =>
                    copyToClipboard(generateShareLink(fuseSavedData.slug))
                  }
                >
                  Share
                </button>
              </div>
              <div className="header-right">
                <a
                  href="/fuselight-beta/"
                  className="btn btn--primary app-link"
                >
                  Use the App
                </a>
                {loadingPitch ? (
                  <LoadingAnimationSimple />
                ) : (
                  <>
                    {pitchLink ? (
                      <a
                        className="btn btn--secondary-outline"
                        href={pitchLink}
                        target="_blank"
                      >
                        View Pitch
                      </a>
                    ) : (
                      <button
                        className="btn btn--secondary-outline"
                        onClick={openCreatePitch}
                      >
                        Create Pitch
                      </button>
                    )}
                  </>
                )}
                <button
                  className="btn btn--tertiary ask-button"
                  onClick={handleToggleSidebar}
                  disabled={
                    loadingScore ||
                    loadingData ||
                    loadingContent ||
                    loadingTimeSeries ||
                    loadingGeoData
                  }
                >
                  <img
                    src={
                      ENV === 'development'
                        ? `${SITE_URL}/app/themes/hahn/images/icons/chat-icon.svg`
                        : `${SITE_URL}/wp-content/themes/hahn/images/icons/chat-icon.svg`
                    }
                    alt="Message Icon"
                    className="message-icon"
                    width={30}
                  />
                  <span>Ask me anything</span>
                </button>
              </div>
            </div>
            <div className="fuse-header-buttons-mobile">
              <div className="buttons-left">
                <button
                  className="btn btn--primary-outline back-button"
                  onClick={
                    backInfo && backInfo.ID
                      ? () => fetchProject(backInfo)
                      : () => handleViewChange('fusions')
                  }
                >
                  Back
                </button>
              </div>
              <div className="buttons-right">
                {loadingPitch ? (
                  <LoadingAnimationSimple />
                ) : (
                  <>
                    {pitchLink ? (
                      <a
                        className="btn btn--secondary-outline"
                        href={pitchLink}
                        target="_blank"
                      >
                        View Pitch
                      </a>
                    ) : (
                      <button
                        className="btn btn--secondary-outline"
                        onClick={openCreatePitch}
                      >
                        Create Pitch
                      </button>
                    )}
                  </>
                )}
                <button
                  className="btn btn--secondary-outline"
                  onClick={() =>
                    copyToClipboard(generateShareLink(fuseSavedData.slug))
                  }
                >
                  Share
                </button>
                <button
                  className="btn btn--tertiary ask-button"
                  onClick={handleToggleSidebar}
                  disabled={
                    loadingScore ||
                    loadingData ||
                    loadingContent ||
                    loadingTimeSeries ||
                    loadingGeoData
                  }
                >
                  <img
                    src={
                      ENV === 'development'
                        ? `${SITE_URL}/app/themes/hahn/images/icons/chat-icon.svg`
                        : `${SITE_URL}/wp-content/themes/hahn/images/icons/chat-icon.svg`
                    }
                    alt="Message Icon"
                    className="message-icon"
                    width={30}
                  />
                  <span>Chat</span>
                </button>
              </div>
            </div>
            <div className="fuse-content">
              {helpOpen && (
                <div
                  className={`fuse-help-modal-container step-${currentStep}`}
                >
                  <FuseHelpModal
                    classes="fuse-help-modal help-modal-6"
                    clickClose={endHelpTutorial}
                    clickNext={nextHelpStep}
                    clickPrev={prevHelpStep}
                    currentStep={currentStep}
                    totalSteps={totalSteps}
                  >
                    <div className="mobile-help-content">
                      <img
                        src={
                          ENV === 'development'
                            ? `${SITE_URL}/app/themes/hahn/images/tutorial/fuse-tutorial-6.webp`
                            : `${SITE_URL}/wp-content/themes/hahn/images/tutorial/fuse-tutorial-6.webp`
                        }
                        alt="Fuselight Logo Icon"
                        className="header-logo-icon"
                        width={300}
                      />
                    </div>
                    <p>
                      Click here to chat with FuseLight<sup>SM</sup> about your
                      fusion. Ask for explanations about any detail that’s
                      unclear. Look for deeper info and insights. See what other
                      ideas or comparisons there may be. Or just have a fun
                      conversation.{' '}
                    </p>
                  </FuseHelpModal>
                  <FuseHelpModal
                    classes="fuse-help-modal help-modal-7"
                    clickClose={endHelpTutorial}
                    clickNext={nextHelpStep}
                    clickPrev={prevHelpStep}
                    currentStep={currentStep}
                    totalSteps={totalSteps}
                  >
                    <p>
                      Congrats! You may have just found your new favorite
                      feature.
                    </p>
                    <p>
                      In a single click, you can automatically generate your
                      pitch as a fully editable presentation deck, complete with
                      every stat, detail, and image in your fusion board and
                      more.
                    </p>
                    <p>
                      You can even create a pitch with multiple brand fusions by
                      using this feature from the Launchplan.
                    </p>
                  </FuseHelpModal>
                  <FuseHelpModal
                    classes="fuse-help-modal help-modal-8 mobile-help-modal-7"
                    clickClose={endHelpTutorial}
                    clickNext={nextHelpStep}
                    clickPrev={prevHelpStep}
                    currentStep={currentStep}
                    totalSteps={totalSteps}
                    style={{ left: `${headerLeftWidth}px` }}
                  >
                    <div className="mobile-help-content">
                      <img
                        src={
                          ENV === 'development'
                            ? `${SITE_URL}/app/themes/hahn/images/tutorial/fuse-tutorial-7.webp`
                            : `${SITE_URL}/wp-content/themes/hahn/images/tutorial/fuse-tutorial-7.webp`
                        }
                        alt="Fuselight Logo Icon"
                        className="header-logo-icon"
                        width={300}
                      />
                    </div>
                    <p>
                      Brand fusions are fun. And so is sharing. Click here to
                      instantly show just how much media potential your brand
                      fusion has to your closest confidants, colleagues and
                      collaborators, your fiercest fans and friends, or even
                      your next-door neighbor. (The nice one.)
                    </p>
                  </FuseHelpModal>
                  <div className="fuse-content-left">
                    <div className="fuse-content-left-top">
                      <div className="fuse-content-left-top-left">
                        <FuseHelpModal
                          classes="fuse-help-modal help-modal-1"
                          clickClose={endHelpTutorial}
                          clickNext={nextHelpStep}
                          clickPrev={prevHelpStep}
                          currentStep={currentStep}
                          totalSteps={totalSteps}
                        >
                          <div className="mobile-help-content">
                            <img
                              src={
                                ENV === 'development'
                                  ? `${SITE_URL}/app/themes/hahn/images/tutorial/fuse-tutorial-1.webp`
                                  : `${SITE_URL}/wp-content/themes/hahn/images/tutorial/fuse-tutorial-1.webp`
                              }
                              alt="Fuselight Logo Icon"
                              className="header-logo-icon"
                              width={300}
                            />
                          </div>
                          <p>
                            This is the score or power-upon-launch of the
                            fusion. It is rooted in data science and shows you
                            how much more media exposure the brand will get as a
                            percentage (or multiplier) of a typical month.
                          </p>
                        </FuseHelpModal>
                      </div>
                      <div className="fuse-content-left-top-right">
                        <FuseHelpModal
                          classes="fuse-help-modal help-modal-2"
                          clickClose={endHelpTutorial}
                          clickNext={nextHelpStep}
                          clickPrev={prevHelpStep}
                          currentStep={currentStep}
                          totalSteps={totalSteps}
                        >
                          <div className="mobile-help-content">
                            <img
                              src={
                                ENV === 'development'
                                  ? `${SITE_URL}/app/themes/hahn/images/tutorial/fuse-tutorial-2.webp`
                                  : `${SITE_URL}/wp-content/themes/hahn/images/tutorial/fuse-tutorial-2.webp`
                              }
                              alt="Fuselight Logo Icon"
                              className="header-logo-icon"
                              width={300}
                            />
                          </div>
                          <p>
                            <strong style={{ color: '#00EDFF' }}>
                              Typical Monthly Mentions
                            </strong>
                            <br />
                            The number of mentions the brand being promoted
                            usually gets.
                          </p>
                          <p>
                            <strong style={{ color: '#00EDFF' }}>
                              Fusion Lift
                            </strong>{' '}
                            <br />
                            This is the the expected rise in mentions, reach and
                            frequency which gives you a probable view of just
                            how powerful this partnership would be directly out
                            of the gates.
                          </p>
                        </FuseHelpModal>
                      </div>
                    </div>
                    <div className="fuse-content-left-bottom">
                      <FuseHelpModal
                        classes="fuse-help-modal help-modal-5"
                        clickClose={endHelpTutorial}
                        clickNext={nextHelpStep}
                        clickPrev={prevHelpStep}
                        currentStep={currentStep}
                        totalSteps={totalSteps}
                      >
                        <div className="mobile-help-content">
                          <img
                            src={
                              ENV === 'development'
                                ? `${SITE_URL}/app/themes/hahn/images/tutorial/fuse-tutorial-5.webp`
                                : `${SITE_URL}/wp-content/themes/hahn/images/tutorial/fuse-tutorial-5.webp`
                            }
                            alt="Fuselight Logo Icon"
                            className="header-logo-icon"
                            width={300}
                          />
                        </div>
                        <p>
                          These are automatically generated social post ideas to
                          help you visualize how your brand fusion might come to
                          life.
                        </p>
                        <p>
                          You can click on each to get a closer look, save them
                          and/or use the{' '}
                          <strong style={{ color: '#ff8b00' }}>Share</strong>{' '}
                          feature, above, to spread the fun.
                        </p>
                      </FuseHelpModal>
                    </div>
                  </div>
                  <div className="fuse-content-right">
                    <div className="fuse-content-right-top">
                      <FuseHelpModal
                        classes="fuse-help-modal help-modal-3"
                        clickClose={endHelpTutorial}
                        clickNext={nextHelpStep}
                        clickPrev={prevHelpStep}
                        currentStep={currentStep}
                        totalSteps={totalSteps}
                      >
                        <div className="mobile-help-content">
                          <img
                            src={
                              ENV === 'development'
                                ? `${SITE_URL}/app/themes/hahn/images/tutorial/fuse-tutorial-3.webp`
                                : `${SITE_URL}/wp-content/themes/hahn/images/tutorial/fuse-tutorial-3.webp`
                            }
                            alt="Fuselight Logo Icon"
                            className="header-logo-icon"
                            width={300}
                          />
                        </div>
                        <p>
                          You can see the historical public interest for each
                          brand singularly or together. This can give you a
                          sense of trajectory or timing in popularity which can
                          help when comparing partners.
                        </p>
                      </FuseHelpModal>
                    </div>
                    <div className="fuse-content-right-bottom">
                      <FuseHelpModal
                        classes="fuse-help-modal help-modal-4"
                        clickClose={endHelpTutorial}
                        clickNext={nextHelpStep}
                        clickPrev={prevHelpStep}
                        currentStep={currentStep}
                        totalSteps={totalSteps}
                      >
                        <div className="mobile-help-content">
                          <img
                            src={
                              ENV === 'development'
                                ? `${SITE_URL}/app/themes/hahn/images/tutorial/fuse-tutorial-4.webp`
                                : `${SITE_URL}/wp-content/themes/hahn/images/tutorial/fuse-tutorial-4.webp`
                            }
                            alt="Fuselight Logo Icon"
                            className="header-logo-icon"
                            width={300}
                          />
                        </div>
                        <p>
                          You can also view the public interest for both brands
                          by geography. This can give you deeper insight into
                          where your brand fusion matters most.
                        </p>
                      </FuseHelpModal>
                    </div>
                  </div>
                </div>
              )}
              <div className="fuse-content-left">
                <div className="fuse-content-left-top">
                  <div className="fuse-content-left-top-left">
                    <IgnitionThrustContext
                      loadingScore={loadingScore}
                      ignitionContext={ignitionContext}
                      ignitionThrust={ignitionThrust}
                      handleRetry={handleRetry}
                    />
                  </div>
                  <div className="fuse-content-left-top-right">
                    <FuseData
                      predictedMediaMentions={predictedMediaMentions}
                      predictedImpressions={predictedImpressions}
                      avgMonthlyMentions={avgMonthlyMentions}
                      frequency={frequency}
                      brandName={brandName}
                      celebrityName={celebrityName}
                      ignitionThrust={ignitionThrust}
                      ignitionContext={ignitionContext}
                      loadingData={loadingData}
                    />
                  </div>
                </div>
                <div className="fuse-content-left-bottom">
                  <FusionCreator
                    fuse={fusionCreatorData}
                    isNewFuse={isNewFuse}
                    handleOpenModal={handleOpenModal}
                    loadingContent={loadingContent}
                    pinImage={pinImage}
                    fuseShareId={fuseShareId}
                    ideaID={ideaID}
                    handleRetry={handleRetry}
                  />
                </div>
              </div>
              <div className="fuse-content-right">
                <div className="fuse-content-right-top">
                  <HistoricalData
                    loadingTimeSeries={loadingTimeSeries}
                    brandTimeSeries={brandTimeSeries}
                    celebrityTimeSeries={celebrityTimeSeries}
                    brandName={brandName}
                    celebName={celebrityName}
                    handleRetry={handleRetry}
                  />
                </div>
                <div className="fuse-content-right-bottom">
                  <GeoData
                    loadingGeoData={loadingGeoData}
                    brandGeoData={brandGeoData}
                    celebrityGeoData={celebrityGeoData}
                    brandName={brandName}
                    celebName={celebrityName}
                    handleRetry={handleRetry}
                  />
                </div>
              </div>
              <div className="fusion-creator-mobile">
                <FusionCreatorMobile
                  fuse={fusionCreatorData}
                  isNewFuse={isNewFuse}
                  handleOpenModal={handleOpenModal}
                  loadingContent={loadingContent}
                  pinImage={pinImage}
                  fuseShareId={fuseShareId}
                  ideaID={ideaID}
                  handleRetry={handleRetry}
                />
              </div>
              <div
                className={
                  fuseSidebarOpen
                    ? 'fuse-sidebar-container sidebar-open'
                    : 'fuse-sidebar-container'
                }
              >
                <div className="fuse-sidebar">
                  <div className="mr-fusion-interface">
                    <div className="mr-fusion-conversation">
                      {mrFusionChat.messages.map((message, index) => (
                        <p key={index}>{message}</p>
                      ))}
                      {mrFusionThinking && (
                        <div className="mr-fusion-thinking">
                          <LoadingAnimationSimple />
                        </div>
                      )}
                      <div
                        style={{ float: 'left', clear: 'both' }}
                        ref={messagesEndRef}
                      ></div>
                    </div>
                    <div className="user-response">
                      <input
                        type="text"
                        name="message"
                        placeholder="Tell me about this geo data"
                        value={inputs.message}
                        onChange={handleChange}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter' && !event.shiftKey) {
                            sendMessage();
                          }
                        }}
                      />
                      <button onClick={sendMessage}>
                        <i class="far fa-paper-plane"></i>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Fuse;
